import { PROCESSED_NOTIFICATION_KINDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { ActiveUserSetNotificationRow } from './ActiveUserSetNotificationRow';
import { AddedAdvisorNotificationRow } from './AddedAdvisorNotificationRow';
import { NotificationGroup } from './NotificationGroup';
import { NotificationItemRow } from './NotificationItemRow';
import { NotificationsEmptyState } from './NotificationsEmptyState';
import { useNewDocuments } from '~/features/dashboard/hooks/useNewDocuments';
import { useResubmissions } from '~/features/dashboard/hooks/useResubmissions';
import { useUnreadConsorsNotes } from '~/features/dashboard/hooks/useUnreadConsorsNotes';
import { beginningOfTime } from '~/features/dashboard/utils/resubmissionDateRange';
import { requestResubmissionRecheck } from '~/features/leadDetails/stores/resubmissionRecheckStore';
import { useUnprocessedActiveUserSetLeads } from '~/features/notifications/hooks/useUnprocessedActiveUserSetLeads';
import { useUnprocessedAddedAssignedUserLeads } from '~/features/notifications/hooks/useUnprocessedAddedAssignedUserLeads';
import { getNotificationLinkOptions } from '~/features/notifications/utils/getNotificationLinkOptions';
import { dateFormat, timeFormat } from '~/lib/i18n';

type Props = {
    onNavigate: () => void;
};

const fullName = (personendaten?: { vorname?: string | null; nachname?: string | null } | null) =>
    `${personendaten?.vorname ?? ''} ${personendaten?.nachname ?? ''}`.trim();

export const UnprocessedTab: React.FC<Props> = ({ onNavigate }) => {
    const { t } = useTranslation();
    // All overdue resubmissions (not just today's), kept current by notification pushes and the due timer.
    const { data: resubmissions } = useResubmissions({ start: beginningOfTime() });
    const { data: newDocuments } = useNewDocuments();
    const { data: unreadConsorsNotes } = useUnreadConsorsNotes();
    const addedAdvisorItems = useUnprocessedAddedAssignedUserLeads();
    const activeUserSetItems = useUnprocessedActiveUserSetLeads();

    const resubmissionItems = resubmissions ?? [];
    const documentItems = (newDocuments ?? []).filter((lead) => lead.unverifiedDocuments > 0);
    const bankReportItems = (unreadConsorsNotes ?? []).filter((entry) => entry.notes.length > 0);

    if (
        resubmissionItems.length === 0 &&
        documentItems.length === 0 &&
        bankReportItems.length === 0 &&
        addedAdvisorItems.length === 0 &&
        activeUserSetItems.length === 0
    ) {
        return <NotificationsEmptyState />;
    }

    return (
        <div className="flex flex-col divide-y divide-grey-stroke max-h-80 overflow-y-auto overflow-x-hidden">
            {resubmissionItems.length > 0 && (
                <NotificationGroup kind={PROCESSED_NOTIFICATION_KINDS.RESUBMISSION}>
                    {resubmissionItems.map((resubmission) => (
                        <NotificationItemRow
                            key={resubmission.id}
                            previewName={fullName(resubmission.antragsteller1.personendaten)}
                            // Date and time the resubmission is due, shown next to the name; the date line
                            // below is when the resubmission was created.
                            previewDetail={
                                resubmission.resubmission
                                    ? `${dateFormat(new Date(resubmission.resubmission))} - ${timeFormat(
                                          new Date(resubmission.resubmission),
                                      )} ${t('TOP_HEADER.NOTIFICATIONS.DATE')}`
                                    : ''
                            }
                            timestamp={resubmission.resubmissionCreatedAt}
                            link={getNotificationLinkOptions(
                                PROCESSED_NOTIFICATION_KINDS.RESUBMISSION,
                                resubmission.id,
                            )}
                            // lead page may already be open/cached - recheck the due resubmission on fresh data
                            onNavigate={() => {
                                requestResubmissionRecheck(resubmission.id);
                                onNavigate();
                            }}
                        />
                    ))}
                </NotificationGroup>
            )}
            {documentItems.length > 0 && (
                <NotificationGroup kind={PROCESSED_NOTIFICATION_KINDS.DOCUMENT}>
                    {documentItems.map((document) => (
                        <NotificationItemRow
                            key={document.id}
                            previewName={fullName(document.antragsteller1.personendaten)}
                            previewDetail={t('TOP_HEADER.NOTIFICATIONS.DOCUMENTS_SUBTITLE', {
                                count: document.unverifiedDocuments,
                            })}
                            timestamp={document.newestUnverifiedDocumentDate}
                            link={getNotificationLinkOptions(PROCESSED_NOTIFICATION_KINDS.DOCUMENT, document.id)}
                            onNavigate={onNavigate}
                        />
                    ))}
                </NotificationGroup>
            )}
            {bankReportItems.length > 0 && (
                <NotificationGroup kind={PROCESSED_NOTIFICATION_KINDS.BANK_REPORT}>
                    {bankReportItems.map((bankReport) => (
                        <NotificationItemRow
                            key={`${bankReport.loanRequestId}-${bankReport.subscriptionIdentifier}`}
                            previewName={fullName(bankReport.antragsteller1.personendaten)}
                            previewDetail={bankReport.notes[0]?.data.content ?? ''}
                            tooltipItems={bankReport.notes.map((note) => ({
                                content: note.data.content,
                                id: note.data.id,
                            }))}
                            timestamp={bankReport.notes[0]?.data.creationDate ?? bankReport.createdAt}
                            link={getNotificationLinkOptions(
                                PROCESSED_NOTIFICATION_KINDS.BANK_REPORT,
                                bankReport.loanRequestId,
                            )}
                            onNavigate={onNavigate}
                        />
                    ))}
                </NotificationGroup>
            )}
            {addedAdvisorItems.length > 0 && (
                <NotificationGroup kind={PROCESSED_NOTIFICATION_KINDS.ASSIGNED_USER_ADDED}>
                    {addedAdvisorItems.map((lead) => (
                        <AddedAdvisorNotificationRow key={lead.id} lead={lead} onNavigate={onNavigate} />
                    ))}
                </NotificationGroup>
            )}
            {activeUserSetItems.length > 0 && (
                <NotificationGroup kind={PROCESSED_NOTIFICATION_KINDS.ACTIVE_USER_SET}>
                    {activeUserSetItems.map((lead) => (
                        <ActiveUserSetNotificationRow key={lead.id} lead={lead} onNavigate={onNavigate} />
                    ))}
                </NotificationGroup>
            )}
        </div>
    );
};
